import { useState, useRef, useEffect } from "react";
import { BRAND } from "./brand";
import useIsMobile, { useIsTablet } from "../hooks/useIsMobile";
import HeroSlide from "./slides/HeroSlide";
import PainPointsSlide from "./slides/PainPointsSlide";
import DemandSlide from "./slides/DemandSlide";
import DemoSlide from "./slides/DemoSlide";
import CaseStudySlide from "./slides/CaseStudySlide";
import AcceptSlide from "./slides/AcceptSlide";

function NavDots({ current, onNav }) {
  const labels = [
    "Proposal",
    "Your Needs",
    "Industry",
    "AI Demo",
    "Results",
    "Accept",
  ];
  return (
    <div
      style={{
        position: "fixed",
        right: 20,
        top: "50%",
        transform: "translateY(-50%)",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        zIndex: 100,
      }}
    >
      {labels.map((l, i) => (
        <button
          key={i}
          onClick={() => onNav(i)}
          title={l}
          style={{
            width: current === i ? 32 : 8,
            height: 8,
            borderRadius: 4,
            border: "none",
            cursor: "pointer",
            padding: 0,
            background:
              current === i ? BRAND.gradient : "rgba(148,163,184,0.25)",
            transition: "all 0.3s",
          }}
        />
      ))}
    </div>
  );
}

export default function ProposalAgreement({ proposalData, mode }) {
  const [current, setCurrent] = useState(0);
  const ref = useRef(null);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isDemo = mode === "demo";

  const data = {
    clientName: "Restaurant Name",
    locations: "1",
    monthlyFee: "$399/mo",
    setupFee: "$499",
    minutesIncluded: "1,000 min",
    excedentCost: "$0.30/min",
    addOns: "N/A",
    preparedBy: "Lucas Espina, CEO",
    date: new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }),
    painPoints: [
      {
        pain: "Missed calls during peak dinner service",
        impact:
          "AI answers every call instantly — zero missed revenue during rush",
        icon: "📵",
        metric: "100%",
        metricLabel: "calls answered",
      },
      {
        pain: "Staff pulled from floor to answer phones",
        impact:
          "Hosts stay on the floor — AI handles phone completely",
        icon: "👥",
        metric: "120+ hrs",
        metricLabel: "saved monthly",
      },
      {
        pain: "After-hours calls go to voicemail",
        impact:
          "24/7 AI captures reservations and orders even at 2am",
        icon: "🌙",
        metric: "30%",
        metricLabel: "revenue recovered",
      },
      {
        pain: "Catering & event inquiries lost in the shuffle",
        impact:
          "AI sends menus, packages & books instantly via SMS",
        icon: "🎉",
        metric: "+300%",
        metricLabel: "event bookings",
      },
    ],
    ...proposalData,
  };

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const h = () => setCurrent(Math.round(c.scrollTop / c.clientHeight));
    c.addEventListener("scroll", h);
    return () => c.removeEventListener("scroll", h);
  }, []);

  const nav = (i) =>
    ref.current?.scrollTo({
      top: i * ref.current.clientHeight,
      behavior: "smooth",
    });

  return (
    <div
      ref={ref}
      style={{
        width: "100%",
        height: "100vh",
        height: "100dvh",
        overflowY: "scroll",
        scrollSnapType: "y mandatory",
        scrollBehavior: "smooth",
      }}
    >
      <style>{`
        @font-face {
          font-family: 'Manrope';
          src: url('/fonts/Manrope-Variable.ttf') format('truetype-variations');
          font-weight: 200 800;
          font-display: swap;
        }
        @font-face {
          font-family: 'SF Pro Rounded';
          src: url('/fonts/SF-Pro-Rounded-Regular.otf') format('opentype');
          font-weight: 400;
          font-display: swap;
        }
        @font-face {
          font-family: 'SF Pro Rounded';
          src: url('/fonts/SF-Pro-Rounded-Medium.otf') format('opentype');
          font-weight: 500;
          font-display: swap;
        }
        @font-face {
          font-family: 'SF Pro Rounded';
          src: url('/fonts/SF-Pro-Rounded-Semibold.otf') format('opentype');
          font-weight: 600;
          font-display: swap;
        }
        @font-face {
          font-family: 'SF Pro Rounded';
          src: url('/fonts/SF-Pro-Rounded-Bold.otf') format('opentype');
          font-weight: 700;
          font-display: swap;
        }
        @font-face {
          font-family: 'SF Pro Rounded';
          src: url('/fonts/SF-Pro-Rounded-Black.otf') format('opentype');
          font-weight: 900;
          font-display: swap;
        }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'SF Pro Rounded', 'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; -webkit-font-smoothing: antialiased; }
        p, li, blockquote { font-family: 'Manrope', 'SF Pro Rounded', -apple-system, sans-serif; }
        @keyframes float { 0%, 100% { transform: scale(1) translate(0,0); } 50% { transform: scale(1.05) translate(10px,-10px); } }
        @keyframes bounce { 0%,20%,50%,80%,100% { transform: translateX(-50%) translateY(0); } 40% { transform: translateX(-50%) translateY(-6px); } 60% { transform: translateX(-50%) translateY(-3px); } }
        @keyframes audioBar { 0% { height: 3px; } 100% { height: 20px; } }
        @keyframes progressBar { 0% { width: 0%; } 100% { width: 100%; } }
        ::-webkit-scrollbar { display: none; }
        html { scrollbar-width: none; }
        @media (max-width: 640px) {
          section { padding: 32px 16px !important; }
        }
        @media (min-width: 641px) and (max-width: 1180px) {
          section { padding: 48px 36px !important; }
        }
      `}</style>
      {!isMobile && <NavDots current={current} onNav={nav} />}
      {!isMobile && current > 0 && (
        <a
          href="https://restohost.ai"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            position: "fixed",
            top: 24,
            right: 28,
            zIndex: 10,
            opacity: 0.75,
            transition: "opacity 0.2s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.75)}
        >
          <img
            src={current === 1 || current === 2 ? "/logos/RH-SVG-6.svg" : "/logos/RH-SVG-2.svg"}
            alt="RestoHost"
            style={{ height: 20, width: "auto", display: "block" }}
          />
        </a>
      )}
      <HeroSlide proposalData={data} onAccept={() => nav(5)} isMobile={isMobile} isTablet={isTablet} isDemo={isDemo} />
      <PainPointsSlide proposalData={data} isMobile={isMobile} isTablet={isTablet} isDemo={isDemo} />
      <DemandSlide isMobile={isMobile} isTablet={isTablet} isDemo={isDemo} />
      <DemoSlide isMobile={isMobile} isTablet={isTablet} isDemo={isDemo} />
      <CaseStudySlide isMobile={isMobile} isTablet={isTablet} onNext={() => nav(5)} isDemo={isDemo} />
      <AcceptSlide proposalData={data} isMobile={isMobile} isTablet={isTablet} isDemo={isDemo} />
    </div>
  );
}
